import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Search, Smartphone, Code, Database, Shield, ArrowRight, CheckCircle2, Server, ShoppingBag, Settings, Zap, LayoutGrid as Layout, Users, Mail, MessageSquare, BarChart, Lock, Fingerprint, Cloud, ShieldCheck, FileLock, ServerCog, Headset, Repeat, Bot, ChevronDown, ChevronUp, X } from 'lucide-react';

export default function Solutions() {
  const [selectedSolution, setSelectedSolution] = useState<number | null>(null);

  const solutions = [
    {
      icon: <Globe className="h-12 w-12 text-blue-500" />,
      title: 'Web Development',
      description: 'Build, Scale, and Transform Your Digital Presence',
      features: [
        'Custom Website Development',
        'Responsive Web Design',
        'E-Commerce Development',
        'CMS Development',
        'Enterprise Web Solutions',
        'Full-Stack Development',
        'API Development & Integration',
        'Website Maintenance & Support'
      ],
      benefits: [
        'User-Centric & SEO-Friendly Designs',
        'Scalable & Future-Proof',
        'Lightning-Fast Performance',
        'Secure & Reliable',
        'Cloud-Ready Development'
      ],
      technologies: ['React', 'Angular', 'Vue.js', 'Node.js', 'Python', 'PHP', '.NET'],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2072',
      stats: [
        { label: 'Projects Delivered', value: '500+' },
        { label: 'Client Satisfaction', value: '98%' },
        { label: 'Performance Increase', value: '40%' }
      ]
    },
    {
      icon: <Smartphone className="h-12 w-12 text-blue-500" />,
      title: 'Mobile App Development',
      description: 'Creating High-Performance, Scalable, and User-Centric Apps',
      features: [
        'iOS App Development',
        'Android App Development',
        'Cross-Platform App Development',
        'Progressive Web Apps (PWA)',
        'Enterprise Mobile Solutions',
        'UI/UX Design for Mobile Apps',
        'Mobile App Testing & QA',
        'App Maintenance & Support'
      ],
      benefits: [
        'End-to-End Development',
        'User-Centric Approach',
        'Scalable & Secure Apps',
        'AI & IoT Integration',
        'Cloud-Based Solutions',
        'Agile Development Methodology'
      ],
      technologies: ['Swift', 'Kotlin', 'React Native', 'Flutter', 'Xamarin'],
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80&w=2072',
      stats: [
        { label: 'App Downloads', value: '1M+' },
        { label: 'App Store Rating', value: '4.8/5' },
        { label: 'User Retention', value: '85%' }
      ]
    },
    {
      icon: <Search className="h-12 w-12 text-blue-500" />,
      title: 'Digital Marketing',
      description: 'Transforming Brands with Data-Driven Strategies',
      features: [
        'Search Engine Optimization (SEO)',
        'Pay-Per-Click (PPC) Advertising',
        'Social Media Marketing (SMM)',
        'Content Marketing',
        'Email Marketing',
        'Conversion Rate Optimization (CRO)',
        'Influencer & Affiliate Marketing',
        'Online Reputation Management (ORM)'
      ],
      benefits: [
        'Data-Driven Strategies',
        'Full-Funnel Marketing Approach',
        'ROI-Focused Campaigns',
        'Certified Digital Experts',
        'Customized Solutions'
      ],
      certifications: ['Google Ads', 'Facebook Blueprint', 'HubSpot Inbound'],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2072',
      stats: [
        { label: 'Traffic Increase', value: '200%' },
        { label: 'Lead Generation', value: '150%' },
        { label: 'ROI', value: '300%' }
      ]
    },
    {
      icon: <Cloud className="h-12 w-12 text-blue-500" />,
      title: 'Cloud Services',
      description: 'Scalable, Secure & Future-Ready Solutions',
      features: [
        'Cloud Infrastructure & Migration',
        'Cloud App Development',
        'Managed Cloud Services',
        'Cloud Security & Compliance',
        'Multi-Cloud & Hybrid Cloud Solutions',
        'Serverless Computing & DevOps',
        'Cloud Backup & Disaster Recovery',
        'Cloud AI & Big Data Solutions'
      ],
      benefits: [
        'Scalability & Cost Efficiency',
        'Enterprise-Grade Security',
        'End-to-End Cloud Solutions',
        'Seamless Integration',
        'Optimized Performance',
        'Global Accessibility'
      ],
      platforms: ['AWS', 'Azure', 'Google Cloud'],
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=2072',
      stats: [
        { label: 'Cost Reduction', value: '40%' },
        { label: 'Uptime', value: '99.9%' },
        { label: 'Performance', value: '60%' }
      ]
    },
    {
      icon: <ShoppingBag className="h-12 w-12 text-blue-500" />,
      title: 'E-Commerce Solutions',
      description: 'Build, Scale, and Succeed in Online Retail',
      features: [
        'Custom E-Commerce Development',
        'E-Commerce Mobile Applications',
        'Marketplace Development',
        'Seamless Payment Integration',
        'Headless E-Commerce Solutions',
        'Personalized Shopping & AI Integration',
        'Inventory & Order Management Systems',
        'Performance Optimization & Security'
      ],
      benefits: [
        'Scalable & Future-Ready',
        'Conversion-Focused UX/UI',
        'Multi-Device Compatibility',
        'Cloud-Powered Efficiency',
        'Ongoing Support & Enhancements'
      ],
      platforms: ['Shopify', 'WooCommerce', 'Magento'],
      image: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&q=80&w=2072',
      stats: [
        { label: 'Sales Growth', value: '200%' },
        { label: 'Conversion Rate', value: '4.5%' },
        { label: 'Cart Recovery', value: '35%' }
      ]
    },
    {
      icon: <Code className="h-12 w-12 text-blue-500" />,
      title: 'Custom Software Development',
      description: 'Tailored Solutions for Business Excellence',
      features: [
        'Enterprise Software Development',
        'Process Automation & Workflow Optimization',
        'AI & Data-Driven Applications',
        'Cloud-Based Software Development',
        'Custom CRM & ERP Development',
        'SaaS Application Development',
        'API Development & Third-Party Integrations',
        'Legacy Software Modernization'
      ],
      benefits: [
        'Fully Tailored & Scalable',
        'Cutting-Edge Technology',
        'User-Centric & Performance-Optimized',
        'End-to-End Development',
        'Security & Compliance'
      ],
      technologies: ['AI/ML', 'Cloud Computing', 'Automation'],
      image: 'https://images.unsplash.com/photo-1571171637578-41bc2dd41cd2?auto=format&fit=crop&q=80&w=2072',
      stats: [
        { label: 'Process Efficiency', value: '75%' },
        { label: 'Time Saved', value: '60%' },
        { label: 'Cost Savings', value: '45%' }
      ]
    },
    {
      icon: <Fingerprint className="h-12 w-12 text-blue-500" />,
      title: 'Digital Identity & Access Management',
      description: 'Fortifying your digital perimeter with robust identity verification and access control.',
      features: [
        'Multi-Factor Authentication (MFA)',
        'Single Sign-On (SSO)',
        'Identity Governance & Administration (IGA)',
        'Privileged Access Management (PAM)',
        'Consumer Identity & Access Management (CIAM)',
        'Adaptive Authentication'
      ],
      benefits: [
        'Enhanced Security Posture',
        'Streamlined User Experience',
        'Regulatory Compliance',
        'Reduced Operational Costs',
        'Improved Auditability'
      ],
      technologies: ['Okta', 'Azure AD', 'Ping Identity', 'SailPoint'],
      image: 'https://images.pexels.com/photos/5380653/pexels-photo-5380653.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      stats: [
        { label: 'User Access Efficiency', value: '99%' },
        { label: 'Compliance Score', value: 'A+' },
        { label: 'Incident Reduction', value: '70%' }
      ]
    },
    {
      icon: <ShieldCheck className="h-12 w-12 text-blue-500" />,
      title: 'Advanced Threat Protection',
      description: 'Proactive defense against sophisticated cyber threats and zero-day attacks.',
      features: [
        'Endpoint Detection & Response (EDR)',
        'Network Detection & Response (NDR)',
        'Security Information & Event Management (SIEM)',
        'Threat Intelligence Integration',
        'Behavioral Analytics',
        'Deception Technologies'
      ],
      benefits: [
        'Early Threat Detection',
        'Minimized Breach Impact',
        'Automated Response',
        'Comprehensive Visibility',
        'Reduced False Positives'
      ],
      technologies: ['CrowdStrike', 'SentinelOne', 'Splunk', 'Microsoft Defender'],
      image: 'https://images.pexels.com/photos/583847/pexels-photo-583847.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      stats: [
        { label: 'Threat Detection Rate', value: '99.9%' },
        { label: 'Response Time', value: 'Minutes' },
        { label: 'Attack Surface Reduction', value: '60%' }
      ]
    },
    {
      icon: <FileLock className="h-12 w-12 text-blue-500" />,
      title: 'Data Privacy & Compliance',
      description: 'Navigating complex data regulations and safeguarding sensitive information.',
      features: [
        'GDPR/CCPA Compliance Solutions',
        'Data Loss Prevention (DLP)',
        'Data Encryption',
        'Privacy Impact Assessments (PIA)',
        'Consent Management Platforms (CMP)',
        'Data Masking & Tokenization'
      ],
      benefits: [
        'Avoid Regulatory Fines',
        'Build Customer Trust',
        'Secure Sensitive Data',
        'Streamlined Compliance Workflows',
        'Data Minimization'
      ],
      technologies: ['OneTrust', 'BigID', 'Varonis'],
      image: 'https://images.pexels.com/photos/313690/pexels-photo-313690.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      stats: [
        { label: 'Compliance Adherence', value: '95%' },
        { label: 'Data Exposure Incidents', value: '0' },
        { label: 'Audit Readiness', value: 'High' }
      ]
    },
    {
      icon: <ServerCog className="h-12 w-12 text-blue-500" />,
      title: 'Secure Cloud Transformation',
      description: 'Migrating to the cloud with security built-in from the ground up.',
      features: [
        'Cloud Security Posture Management (CSPM)',
        'Cloud Workload Protection Platform (CWPP)',
        'Cloud Access Security Broker (CASB)',
        'Infrastructure as Code (IaC) Security',
        'DevSecOps Integration',
        'Cloud Native Security'
      ],
      benefits: [
        'Secure Cloud Adoption',
        'Scalable Security',
        'Cost Optimization',
        'Automated Security Controls',
        'Unified Cloud Visibility'
      ],
      platforms: ['AWS Security Hub', 'Azure Security Center', 'Google Cloud Security Command Center'],
      image: 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      stats: [
        { label: 'Cloud Vulnerabilities Reduced', value: '80%' },
        { label: 'Secure Deployment Rate', value: '99%' },
        { label: 'Cloud Cost Savings', value: '30%' }
      ]
    },
    {
      icon: <Headset className="h-12 w-12 text-blue-500" />,
      title: 'Managed Security Services',
      description: '24/7 expert monitoring and response to protect your digital assets.',
      features: [
        'Security Operations Center (SOC) as a Service',
        'Managed Detection & Response (MDR)',
        'Vulnerability Management as a Service',
        'Incident Response Retainer',
        'Security Awareness Training',
        'Threat Hunting'
      ],
      benefits: [
        'Proactive Threat Management',
        'Reduced Security Burden',
        'Access to Expert Talent',
        'Faster Incident Response',
        'Predictable Security Costs'
      ],
      technologies: ['SIEM/SOAR platforms', 'EDR solutions'],
      image: 'https://images.pexels.com/photos/3153198/pexels-photo-3153198.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      stats: [
        { label: 'Mean Time to Detect (MTTD)', value: '15 min' },
        { label: 'Mean Time to Respond (MTTR)', value: '30 min' },
        { label: 'Security Posture Score', value: '90%' }
      ]
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative h-[500px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=2072)',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/50 to-blue-800/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-center w-full">
            <h1 className="text-5xl font-bold text-white mb-6">
              Enterprise Solutions for Digital Success
            </h1>
            <p className="text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Discover our comprehensive range of digital solutions designed to transform your business and drive growth in the digital age.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 border-2 border-white text-lg font-medium rounded-md text-white hover:bg-white hover:text-blue-600 transition-colors duration-300"
            >
              Schedule a Consultation
              <ArrowRight className="ml-2 h-6 w-6" />
            </Link>
          </div>
        </div>
      </div>

      {/* Solutions Grid - All Visible */}
      <div className="relative py-20 overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
            <div className="absolute top-0 right-10 w-72 h-72 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from our comprehensive suite of digital solutions tailored to accelerate your business growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div
                key={index}
                onClick={() => setSelectedSolution(index)}
                className="group relative bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-2"
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                {/* Gradient Border Effect on Hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>

                <div className="relative h-56 overflow-hidden">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent group-hover:from-blue-900/90 group-hover:via-blue-900/50 transition-all duration-500" />

                  {/* Icon with Animation */}
                  <div className="absolute top-4 right-4">
                    <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl group-hover:bg-white/30 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                      {React.cloneElement(solution.icon as React.ReactElement, {
                        className: 'h-6 w-6 text-white'
                      })}
                    </div>
                  </div>

                  {/* Stats Badge */}
                  <div className="absolute top-4 left-4 px-3 py-1 bg-blue-600/90 backdrop-blur-sm rounded-full text-white text-xs font-semibold">
                    {solution.stats[0].value} {solution.stats[0].label.split(' ')[0]}
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-blue-100 transition-colors duration-300">
                      {solution.title}
                    </h3>
                    <div className="h-1 w-16 bg-blue-500 rounded-full group-hover:w-24 transition-all duration-500"></div>
                  </div>
                </div>

                <div className="p-6 bg-white">
                  <p className="text-gray-600 mb-4 line-clamp-2 group-hover:text-gray-900 transition-colors">
                    {solution.description}
                  </p>

                  {/* Feature Pills */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {solution.features.slice(0, 2).map((feature, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded-full font-medium"
                      >
                        {feature.split(' ')[0]}
                      </span>
                    ))}
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-medium">
                      +{solution.features.length - 2} more
                    </span>
                  </div>

                  {/* View Details CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-blue-600 font-semibold group-hover:text-blue-700 transition-colors">
                      View Details
                    </span>
                    <div className="p-2 rounded-full bg-blue-50 group-hover:bg-blue-600 transition-colors duration-300">
                      <ArrowRight className="h-5 w-5 text-blue-600 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                  </div>
                </div>

                {/* Hover Shine Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal for Solution Details */}
      {selectedSolution !== null && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedSolution(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-6xl w-full my-8 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
              <div className="flex items-center justify-between p-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    {React.cloneElement(solutions[selectedSolution].icon as React.ReactElement, {
                      className: 'h-8 w-8 text-blue-600'
                    })}
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">
                      {solutions[selectedSolution].title}
                    </h2>
                    <p className="text-lg text-gray-600 mt-1">
                      {solutions[selectedSolution].description}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedSolution(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close modal"
                >
                  <X className="h-6 w-6 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="relative h-64 mb-8 rounded-xl overflow-hidden">
                <img
                  src={solutions[selectedSolution].image}
                  alt={solutions[selectedSolution].title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {solutions[selectedSolution].stats.map((stat, statIndex) => (
                  <div
                    key={statIndex}
                    className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl text-center"
                  >
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-gray-700 font-medium">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <CheckCircle2 className="h-6 w-6 text-green-500 mr-2" />
                    Key Features
                  </h3>
                  <div className="space-y-3">
                    {solutions[selectedSolution].features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start space-x-3">
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <CheckCircle2 className="h-6 w-6 text-blue-500 mr-2" />
                    Benefits
                  </h3>
                  <div className="space-y-3">
                    {solutions[selectedSolution].benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-start space-x-3">
                        <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                  {(solutions[selectedSolution].technologies ||
                    solutions[selectedSolution].platforms ||
                    solutions[selectedSolution].certifications) && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="text-lg font-medium text-gray-900 mb-3">
                        {solutions[selectedSolution].technologies ? 'Technologies' :
                         solutions[selectedSolution].platforms ? 'Platforms' :
                         'Certifications'}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {(solutions[selectedSolution].technologies ||
                          solutions[selectedSolution].platforms ||
                          solutions[selectedSolution].certifications)?.map((item, itemIndex) => (
                          <span
                            key={itemIndex}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl"
                >
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <button
                  onClick={() => setSelectedSolution(null)}
                  className="inline-flex items-center px-8 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="relative py-20">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=2072)',
            filter: 'brightness(0.3)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/50 to-blue-800/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Let's work together to create innovative solutions that drive your success
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-lg font-medium rounded-md text-white hover:bg-white hover:text-blue-600 transition-colors duration-300"
              >
                Get in Touch
                <ArrowRight className="ml-2 h-6 w-6" />
              </Link>
              <Link
                to="/work"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white bg-white text-lg font-medium rounded-md text-blue-600 hover:bg-transparent hover:text-white transition-colors duration-300"
              >
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}